import type { FC, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import {
  Button,
  Typography,
  Stack,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'src/components/molecules/ConfirmDialog';
import { TableToolbarShell } from 'src/components/molecules/TableToolbarShell';
import {
  TaskRo,
  useTasksControllerRemoveManyMutation,
  useTasksControllerGenerateOutputMutation,
} from 'src/app/services/api.generated';
import { TableSearchInput } from 'src/components/atoms/TableSearchInput';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router';
import { useAppDispatch } from 'src/app/reduxCustomHooks';
import { api } from 'src/app/services/api';
import { toast } from 'react-toastify';
import {
  patchTaskOutputState,
  WorkspaceFolderQueryKey,
} from 'src/utils/taskProgressStreamUtils';
import { isExportOutdatedError } from 'src/utils/exportOutdated';

type TaskTableToolbarPropsType = {
  rows: TaskRo[];
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  searchInput: string;
  setSearchInput: Dispatch<SetStateAction<string>>;
  folderQueryKey?: WorkspaceFolderQueryKey;
  goToCreateTaskPage?: () => void;
  assignedToMeOnly?: boolean;
  setAssignedToMeOnly?: Dispatch<SetStateAction<boolean>>;
  hideCreateButton?: boolean;
  ocrSearchInput?: string;
  setOcrSearchInput?: Dispatch<SetStateAction<string>>;
  ocrSearchPlaceholder?: string;
  /** "With open markers" filter ("not sure" markers). */
  flush?: boolean;
};

export const TaskTableToolbar: FC<TaskTableToolbarPropsType> = ({
  rows,
  selected,
  setSelected,
  searchInput,
  setSearchInput,
  folderQueryKey,
  goToCreateTaskPage,
  assignedToMeOnly = false,
  setAssignedToMeOnly,
  hideCreateButton = false,
  ocrSearchInput,
  setOcrSearchInput,
  ocrSearchPlaceholder,
  flush = false,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const dispatch = useAppDispatch();

  const [deleteManyTasks, { isLoading: deleteManyTasksLoading }] =
    useTasksControllerRemoveManyMutation();
  const [generateOutput, { isLoading: generateOutputLoading }] =
    useTasksControllerGenerateOutputMutation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const openDialogHandler = () => setShowDialog(true);
  const closeDialogHandler = () => setShowDialog(false);

  const inputChangeHandler = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setSearchInput(event.target.value);

  const selectedRows = selected
    .map((id) => rows.find((row) => row.id === id))
    .filter((row): row is TaskRo => Boolean(row));
  const selectedRowsCanGenerate = selectedRows.filter(
    (row) => row.outputState !== 'GENERATED_OUTPUT'
  );
  const selectedRowsCanDownload = selectedRows.filter(
    (row) => row.outputState === 'GENERATED_OUTPUT'
  );

  const selectedTasksNameRenderHandler = () => {
    const selectedTaskNames = selectedRows.map((row) => row.name);
    let result = selectedTaskNames.slice(0, 3).join(', ');
    if (selectedTaskNames.length > 3) {
      result = `${result} ${t('andOtherTasks', {
        count: selectedTaskNames.length - 3,
      })}`;
    }
    return result;
  };

  const clearButtonOnClick = () => {
    setSelected([]);
  };
  const removeButtonOnClick = () => {
    if (selected.length === 0 || rows.length === 0) return;

    deleteManyTasks({ idsDto: { ids: selected } })
      .unwrap()
      .then(() => {
        setSelected([]);
        closeDialogHandler();
      });
  };

  const generateSelectedOutputOnClick = async () => {
    if (selectedRowsCanGenerate.length === 0) return;
    if (folderQueryKey) {
      selectedRowsCanGenerate.forEach((row) => {
        patchTaskOutputState(
          dispatch,
          folderQueryKey,
          row.id,
          'GENERATING_OUTPUT'
        );
      });
    }
    const results = await Promise.allSettled(
      selectedRowsCanGenerate.map((row) =>
        generateOutput({ generateOutputDto: { id: row.id } }).unwrap()
      )
    );
    const successCount = results.filter((res) => res.status === 'fulfilled')
      .length;
    const failedCount = results.length - successCount;
    toast.info(
      t('bulkGenerateSummary', {
        success: successCount,
        failed: failedCount,
      })
    );
  };

  const downloadSelectedOutputOnClick = async () => {
    if (selectedRowsCanDownload.length === 0 || bulkDownloadLoading) return;
    setBulkDownloadLoading(true);
    let successCount = 0;
    let failedCount = 0;
    let skippedCount = 0;
    let outdatedCount = 0;
    try {
      for (const row of selectedRowsCanDownload) {
        try {
          const outFileUrl = await dispatch(
            api.endpoints.tasksControllerGetTaskOutFileUrl.initiate(
              { taskId: row.id },
              { forceRefetch: true }
            )
          ).unwrap();
          if (!outFileUrl?.url) {
            skippedCount += 1;
            continue;
          }

          const anchor = document.createElement('a');
          document.body.appendChild(anchor);
          anchor.href = outFileUrl.url;
          anchor.download =
            row.name?.split('.').slice(0, -1).join('.') + `(${t('redacted')})`;
          anchor.click();
          anchor.remove();
          successCount += 1;
        } catch (error) {
          if (isExportOutdatedError(error)) {
            // Edited after the export: counted as skipped, not failed.
            outdatedCount += 1;
            skippedCount += 1;
            if (folderQueryKey) {
              patchTaskOutputState(
                dispatch,
                folderQueryKey,
                row.id,
                'OUTDATED_OUTPUT'
              );
            }
            continue;
          }
          failedCount += 1;
        }
      }
      if (outdatedCount > 0) {
        toast.warning(t('exportOutdated'));
      }
      toast.info(
        t('bulkDownloadSummary', {
          success: successCount,
          failed: failedCount,
          skipped: skippedCount,
        })
      );
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  return (
    <>
      <TableToolbarShell flush={flush}>
        {selected.length === 0 ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexWrap: 'wrap', rowGap: 1, flex: 1, minWidth: 0 }}
            >
              <TableSearchInput
                value={searchInput}
                onChange={inputChangeHandler}
                sx={{ width: { xs: '100%', sm: 240 } }}
              />
              {setOcrSearchInput && (
                <TextField
                  size="small"
                  value={ocrSearchInput ?? ''}
                  onChange={(e) => setOcrSearchInput(e.target.value)}
                  placeholder={
                    ocrSearchPlaceholder || String(t('archiveSearchOcr'))
                  }
                  sx={{ width: { xs: '100%', sm: 240 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {setAssignedToMeOnly && (
                <Chip
                  size="small"
                  label={t('assignedToMe')}
                  color={assignedToMeOnly ? 'primary' : 'default'}
                  variant={assignedToMeOnly ? 'filled' : 'outlined'}
                  onClick={() => setAssignedToMeOnly((prev) => !prev)}
                  clickable
                />
              )}
            </Stack>
            {!hideCreateButton && (
              <Button
                size="small"
                onClick={goToCreateTaskPage || (() => navigate('/createTasks'))}
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                {t('newTask')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Typography sx={{ flex: 1, minWidth: 0 }}>
              {selected.length} {t('selectedItems')}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              columnGap={1}
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              <Button
                size="small"
                variant="contained"
                color="inherit"
                sx={{ color: 'common.black' }}
                onClick={clearButtonOnClick}
              >
                {t('deselect')}
              </Button>
              <LoadingButton
                size="small"
                disabled={selectedRowsCanGenerate.length === 0}
                loading={generateOutputLoading}
                onClick={generateSelectedOutputOnClick}
                color="primary"
                variant="outlined"
              >
                {t('generateOutput')}
              </LoadingButton>
              <LoadingButton
                size="small"
                disabled={selectedRowsCanDownload.length === 0}
                loading={bulkDownloadLoading}
                onClick={downloadSelectedOutputOnClick}
                color="primary"
                variant="outlined"
              >
                {t('download')}
              </LoadingButton>
              <LoadingButton
                size="small"
                disabled={selected.length === 0}
                loading={deleteManyTasksLoading}
                onClick={openDialogHandler}
                color="error"
                variant="contained"
              >
                {t('remove')}
              </LoadingButton>
            </Stack>
          </>
        )}
      </TableToolbarShell>
      {showDialog && (
        <ConfirmDialog
          title={t('deleteTask')}
          warningText={t('confirmRemoveManyTaskNoWorkspace', {
            taskName: selectedTasksNameRenderHandler(),
          })}
          dialogProps={{
            open: true,
            onClose: closeDialogHandler,
          }}
          onSubmitClick={removeButtonOnClick}
          submitText={t('removeSelectedItems')}
          isSubmitLoading={false}
        />
      )}
    </>
  );
};
